/**
 * Real-time slash command validation and preview for the agent system prompt editor.
 *
 * Parses the <slash_commands> XML block from the system prompt and shows:
 * - A live preview of detected commands
 * - Validation errors (malformed XML, missing name attributes, etc.)
 * - A help hint when no slash commands block is found
 */

import { AlertTriangle, CheckCircle, HelpCircle, Terminal } from "lucide-react";
import { useMemo, useState } from "react";
import type { SlashCommand } from "@/types";

/** Result of parsing slash commands from a system prompt. */
interface ParseResult {
  commands: SlashCommand[];
  error: string | null;
  hasBlock: boolean;
}

/** Parse a <slash_commands> XML block from a system prompt string. */
function parseSlashCommands(systemPrompt: string): ParseResult {
  if (!systemPrompt) return { commands: [], error: null, hasBlock: false };

  const match = systemPrompt.match(/<slash_commands>([\s\S]*?)<\/slash_commands>/);
  if (!match) return { commands: [], error: null, hasBlock: false };

  const innerXml = match[1].trim();
  if (!innerXml) return { commands: [], error: "The <slash_commands> block is empty.", hasBlock: true };

  // Parse XML using DOMParser (browser-native, no dependencies)
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<root>${innerXml}</root>`, "text/xml");

  // Check for XML parse errors
  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    return {
      commands: [],
      error: "Malformed XML in <slash_commands> block. Check for unclosed tags or invalid characters.",
      hasBlock: true,
    };
  }

  const commandElements = doc.querySelectorAll("command");
  if (commandElements.length === 0) {
    return {
      commands: [],
      error: "No <command> elements found inside <slash_commands>.",
      hasBlock: true,
    };
  }

  const commands: SlashCommand[] = [];
  const warnings: string[] = [];

  commandElements.forEach((el, i) => {
    const name = (el.getAttribute("name") || "").trim();
    const description = (el.getAttribute("description") || "").trim();

    if (!name) {
      warnings.push(`Command #${i + 1} is missing a "name" attribute.`);
      return;
    }

    if (name.includes(" ")) {
      warnings.push(`Command "${name}" contains spaces — command names should be single words or hyphenated.`);
      return;
    }

    if (!description) {
      warnings.push(`Command "/${name}" has no description — users won't know what it does.`);
    }

    commands.push({ name, description });
  });

  const error = warnings.length > 0 ? warnings.join(" ") : null;
  return { commands, error, hasBlock: true };
}

/** Compact preview of parsed slash commands shown below the system prompt textarea. */
export function SlashCommandsPreview({ systemPrompt }: { systemPrompt: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const result = useMemo(() => parseSlashCommands(systemPrompt), [systemPrompt]);

  // No slash commands block at all — show a subtle hint
  if (!result.hasBlock) {
    return (
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="mt-2 flex items-center gap-1.5 text-xs text-content-quaternary hover:text-content-tertiary transition"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        <span>Tip: Add slash commands to give users quick actions</span>
      </button>
    );
  }

  // Has block but with errors
  if (result.error && result.commands.length === 0) {
    return (
      <div className="mt-2 flex items-start gap-2 text-xs text-red-400">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>{result.error}</span>
      </div>
    );
  }

  // Valid commands found
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex items-center gap-1.5 text-xs text-content-tertiary hover:text-content-secondary transition"
      >
        {result.error ? (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
        ) : (
          <CheckCircle className="w-3.5 h-3.5 text-green-400" />
        )}
        <Terminal className="w-3.5 h-3.5" />
        <span>
          {result.commands.length} slash command{result.commands.length !== 1 ? "s" : ""} detected
        </span>
        <span className="text-content-quaternary">{isExpanded ? "Hide" : "Preview"}</span>
      </button>

      {/* Warning message if there are issues */}
      {result.error && (
        <div className="mt-1 flex items-start gap-2 text-xs text-amber-400">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{result.error}</span>
        </div>
      )}

      {/* Expanded preview */}
      {isExpanded && (
        <div className="mt-2 bg-surface-secondary border border-border-secondary rounded-lg overflow-hidden">
          <div className="px-3 py-1.5 text-xs text-content-quaternary border-b border-border-secondary flex items-center gap-1.5">
            <Terminal className="w-3 h-3" />
            Preview — as users will see it
          </div>
          {result.commands.map((cmd, idx) => (
            <div
              key={`${cmd.name}-${idx}`}
              className="flex items-start gap-3 px-3 py-2 border-b border-border-secondary last:border-b-0"
            >
              <span className="text-sm font-mono font-medium text-brand-primary shrink-0">/{cmd.name}</span>
              {cmd.description && <span className="text-xs text-content-quaternary pt-0.5">{cmd.description}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
